import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonVariant,
  DataTable,
  DataTableColumn,
  Pagination,
  PaginationPosition,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { UserRole } from '@hobt/constants';
import { Arrears, PagedArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';
import { Ordering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Ordering';
import { Paging } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Paging';
import { useTableOrdering } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useTableOrdering';
import { useTablePagination } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useTablePagination';
import { AmountField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/AmountField';
import Body from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Body';
import Header from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/Header';
import { StatusField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/common/StatusField';
import { ArrearsFlyout } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/ArrearsFlyout';
import { DeleteArrearsModal } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/DeleteArrearsModal';
import { useArrearsUser } from 'Feature/Arrears/components/ArrearsInquiryPage/core/users/useArrearsUser';
import { useDeleteArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useDeleteArrears';

interface ArrearsTableData {
  id: string;
  status: string;
  reportingPeriodStatus: string;
  reportingPeriodDate: string;
  dueDate: string;
  lenderName: string;
  lenderCode: string;
  amount: number;
}

interface ArrearsTableProps {
  arrears: PagedArrears;
  reloadingArrears: boolean;
  changePaging: (paging: Paging) => void;
  changeOrdering: (ordering: Ordering) => void;
  onArrearsUpdated: (updatedArrears: Arrears) => void;
  onArrearsDeleted: () => void;
}

const ArrearsTable = ({
  arrears,
  reloadingArrears,
  changePaging,
  changeOrdering,
  onArrearsUpdated,
  onArrearsDeleted
}: ArrearsTableProps) => {
  const { t, i18n } = useTranslation('HbtArrearsInquiryPage');

  const [openFlyout, setOpenFlyout] = useState(false);
  const [selectedArrears, setSelectedArrears] = useState<Arrears | undefined>(undefined);
  const [deletingArrearsId, setDeletingArrearsId] = useState<string | undefined>(undefined);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showDeleteSuccessToast, setShowDeleteSuccessToast] = useState(false);

  const { deleteArrears, deletingArrears } = useDeleteArrears();

  const pagination = useTablePagination(arrears.paging, changePaging);
  const ordering = useTableOrdering(Ordering.defaultValue, changeOrdering);

  const tableData: ArrearsTableData[] = arrears.data.map((a) => ({
    id: a.id,
    status: a.status,
    reportingPeriodStatus: a.reportingPeriodStatus,
    reportingPeriodDate: a.reportingPeriodDate,
    dueDate: a.dueDate,
    lenderName: a.lenderName,
    lenderCode: a.lenderCode,
    amount: a.amount
  }));

  const onRowClick = (id: string | number) => {
    setSelectedArrears(arrears.data.find((a) => a.id === id));
    setOpenFlyout(true);
  };

  const onFlyoutClose = () => {
    setSelectedArrears(undefined);
    setOpenFlyout(false);
  };

  const handleOpenDeleteModal = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => {
    setDeletingArrearsId(id);
    event.stopPropagation();
    setOpenDeleteModal(true);
  };

  const user = useArrearsUser();

  const isOpenReportingPeriod = (id: string) => {
    return arrears.data.find((a) => a.id === id)?.reportingPeriodStatus === 'open';
  };

  const isMasterUser = user.roles.includes(UserRole.MasterUser);

  const handleSubmitDelete = async () => {
    const result = await deleteArrears(deletingArrearsId!);
    setOpenDeleteModal(false);
    if (result.isFailure) return;
    onArrearsDeleted();
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Header size="medium">{t('arrearsTable.title')}</Header>
      <DataTable<ArrearsTableData>
        uniqueKey={'id'}
        data={tableData}
        sortedColumn={ordering.sortField}
        sortDirection={ordering.sortDirection}
        isLoading={reloadingArrears}
        onSort={ordering.onColumnSort}
        onRowClick={onRowClick}
        autoTableLayout={true}
        emptyTableMessage={t('arrearsTable.empty')}
      >
        <DataTableColumn displayName={t('arrearsTable.columns.status')} name={'status'}>
          {(data) => <StatusField ok={data === 'active'} status={t(`arrears.status.${data}`)} />}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.reportingPeriodStatus')}
          name={'reportingPeriodStatus'}
          sortable={false}
          filterable={false}
        >
          {(data) => <Body size={'small'}>{t(`reportingPeriod.status.${data}`)}</Body>}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.reportingPeriodDate')}
          name={'reportingPeriodDate'}
        >
          {(data) => <Body size={'small'}>{data}</Body>}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.dueDate')}
          name={'dueDate'}
          sortable={false}
          filterable={false}
        >
          {(data) => <Body size={'small'}>{data}</Body>}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.lenderName')}
          name={'lenderName'}
          sortable={false}
          filterable={false}
        >
          {(data) => <Body size={'small'}>{data}</Body>}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.lenderCode')}
          name={'lenderCode'}
          sortable={false}
          filterable={false}
        >
          {(data) => <Body size={'small'}>{data}</Body>}
        </DataTableColumn>
        <DataTableColumn
          displayName={t('arrearsTable.columns.arrearsAmount')}
          name={'amount'}
          sortable={false}
          filterable={false}
          headerAlignment={'end'}
          rowAlignment={'end'}
        >
          {(data) => <AmountField amount={data} language={i18n.language} />}
        </DataTableColumn>
        {isMasterUser && (
          <DataTableColumn
            displayName={t('arrearsTable.columns.actions')}
            name={'id'}
            sortable={false}
            filterable={false}
            headerAlignment={'end'}
            rowAlignment={'center'}
          >
            {(id: string) =>
              isOpenReportingPeriod(id) && (
                <Button
                  name={t('arrearsTable.deleteSection.delete')}
                  ariaText={t('arrearsTable.deleteSection.delete')}
                  text={t('arrearsTable.deleteSection.delete')}
                  variant={ButtonVariant.TERTIARY}
                  onClick={(event) => handleOpenDeleteModal(event, id)}
                ></Button>
              )
            }
          </DataTableColumn>
        )}
      </DataTable>
      <Pagination
        currentPageNumber={pagination.currentPageNumber}
        totalPageNumber={pagination.totalPageNumber}
        isCondensed={true}
        isOnTablet={false}
        onPageChange={pagination.onPageChange}
        numberOfItemsOnPage={pagination.numberOfItemsOnPage}
        onItemsPerPageChange={pagination.onItemsPerPageChange}
        totalItemNumber={pagination.totalItemNumber}
        paginationPosition={PaginationPosition.RIGHT}
      />
      {selectedArrears && (
        <ArrearsFlyout
          arrears={selectedArrears}
          openFlyout={openFlyout}
          onFlyoutClose={onFlyoutClose}
          onArrearsUpdated={onArrearsUpdated}
        />
      )}
      <DeleteArrearsModal
        isActive={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleSubmitDelete}
        loading={deletingArrears}
      />
      <ToastNotification
        open={showDeleteSuccessToast}
        header={{
          value: t('arrearsTable.deleteSection.successToastSection.delete.success.header')
        }}
        subHeader={{
          value: t('arrearsTable.deleteSection.successToastSection.delete.success.message')
        }}
        variant={ToastNotificationVariant.INFO}
        icon="icon_info_outlined"
        onClose={() => {
          setShowDeleteSuccessToast(false);
        }}
        dismissMode={'auto'}
      />
    </Box>
  );
};

export default ArrearsTable;
